/**
 * Stale-bundle recovery — auto-reload once when a lazy-loaded chunk fails to
 * fetch.
 *
 * After a deploy, Vite rehashes the lazy route chunks. A tab still running the
 * previous `index.js` references the OLD chunk filenames, which no longer exist
 * on the CDN — so `import('./SomePage')` rejects with "Failed to fetch
 * dynamically imported module". This is cache churn, not a code bug; the cure
 * is to reload and pick up the current bundle.
 *
 * Used in two places so it covers every route:
 *   - main.tsx listens for Vite's `vite:preloadError` (fires before render),
 *   - RouteErrorBoundary calls reloadOnceForStaleChunk() when it catches one
 *     at render time.
 *
 * Guarded by a sessionStorage timestamp so a genuinely broken chunk (offline,
 * real 500) reloads at most once per 10s window instead of hard-looping, while
 * a LATER deploy in the same session can still trigger a fresh recovery.
 */

const RELOAD_TS_KEY = 'ppm-stale-chunk-reload-ts'
const RELOAD_WINDOW_MS = 10_000

/** True when `err` looks like a failed dynamic-import / preload (stale bundle). */
export function isChunkLoadError(err: unknown): boolean {
  const msg =
    typeof err === 'string'
      ? err
      : err instanceof Error
        ? err.message
        : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : ''
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg) ||
    /unable to preload css/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  )
}

/**
 * Reload the page to fetch the current bundle — but only if we haven't already
 * tried within the last few seconds (loop guard). Returns true if a reload was
 * triggered (callers can short-circuit their fallback UI).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0)
    if (Date.now() - last < RELOAD_WINDOW_MS) return false // already tried — don't loop
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
  } catch {
    /* storage disabled — fall through and still reload once */
  }
  window.location.reload()
  return true
}

/** Install the global preload-error listener (call once at app entry). */
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Prevent Vite's default (a thrown error) and recover by reloading.
    event.preventDefault()
    reloadOnceForStaleChunk()
  })
}
